// Single source of truth for "which sidebar row is selected".
//
// Every call site used to test `pathname.includes(item.path)`, which is a substring
// test and got two things wrong:
//   - Section containers carry `path: '/'`, and every pathname contains '/', so a
//     container counted as selected on every screen.
//   - A parent path is a prefix of its children, so `/organization` matched while on
//     `/organization/report` — two rows lit up at once.
//
// The sidebar can only ever point at one place, so this module resolves exactly one
// winning path for the current URL: the longest declared path that owns it. Rows then
// compare their own path against that winner.

import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { adminSidebarItems, helpItem, manageOrgItem, SidebarItem, sidebarItems } from './constants'

/** `'/'` marks a group container, not a destination, so it can never be selected. */
const isDestination = (path?: string): boolean => !!path && path !== '/'

/** Paths are declared with an optional query (e.g. Stacks); only the path can match. */
const basePathOf = (path: string): string => path.split('?')[0]

/**
 * Boundary-aware match: `/teams` owns `/teams` and `/teams/5`, but not
 * `/teams-archive` — which a substring test would wrongly claim.
 */
export const matchesSidebarPath = (pathname: string, itemPath?: string): boolean => {
  if (!isDestination(itemPath)) return false
  const base = basePathOf(itemPath!)
  return pathname === base || pathname.startsWith(`${base}/`)
}

const collectPaths = (items: SidebarItem[], out: string[] = []): string[] => {
  for (const item of items) {
    if (isDestination(item.path)) out.push(basePathOf(item.path))
    if (item.children?.length) collectPaths(item.children, out)
  }
  return out
}

// Every path the sidebar can point at — both nav lists plus the two rows rendered
// outside them (Manage your Org, Help). Computed once: the lists are static.
const ALL_SIDEBAR_PATHS: string[] = Array.from(
  new Set(collectPaths([...sidebarItems, ...adminSidebarItems, manageOrgItem, helpItem]))
)

/**
 * The one path that owns the current URL. Longest match wins, so a parent never
 * lights up beside the more specific child that actually matches.
 */
export const resolveActiveSidebarPath = (pathname: string): string | null => {
  let winner: string | null = null
  for (const path of ALL_SIDEBAR_PATHS) {
    if (matchesSidebarPath(pathname, path) && (winner === null || path.length > winner.length)) {
      winner = path
    }
  }
  return winner
}

export const useActiveSidebarPath = (): string | null => {
  const { pathname } = useLocation()
  return useMemo(() => resolveActiveSidebarPath(pathname), [pathname])
}

/** True only for the row that owns the current URL — at most one row per render. */
export const isSidebarItemActive = (item: SidebarItem, activePath: string | null): boolean =>
  isDestination(item.path) && basePathOf(item.path) === activePath
